import React, { FC, useEffect, useState } from "react";
import FlexLine from "../../../../../components/elements/flexLine/FlexLine";
import Container from "../../../../../components/elements/container/Container";
import Sticker from "../../../../../components/elements/sticker/Sticker";
import st from "./styles.module.scss";
import Category from "../../components/category/Category";
import LinearChart from "../../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import { applyTip, checkWindowSize, monthsForLocale, wideFontSize32 } from "@utils/utils";
import { stickerLoadingWithCrop } from "../../../phases";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../../../store";
import { ApiSelect } from "@baseTypes/baseType";
import * as Highcharts from "highcharts";
import { identity } from "fp-ts/es6/function";
import {
    ExpensesDetalizationData,
    ExpensesDetalizationState,
} from "../../../../../store/reducers/dashboard/expenses/detalization";
import { useParams } from "react-router-dom";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { Empty } from "./empty";
import { array } from "fp-ts";
import BaseChartTooltip from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltip/BaseChartTooltip";
import {
    RowWithAreaFormatter
} from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltip/rowFormatters/RowFormatters";
import PlanFactLegend from "@elements/planFactLegend/PlanFactLegend";
import Badge from "@pages/dashboard/expenses/components/badge/Badge";
import { formatNumberIntl } from "@utils/format/number";

const Day1Tick = 1000 * 60 * 60 * 24;
const defaultOptions: (x: ExpensesDetalizationData, monthId: number, categories: ExpensesDetalizationData[]) => Highcharts.Options = (category, monthId, categories: ExpensesDetalizationData[]) => ({
    title: {
        text: ''
    },
    legend: {
        enabled: false
    },
    chart: {
        height: '33%' // 16:9 ratio
    },
    xAxis:
        {
            type: 'datetime',
            labels: {
                style: {
                    fontSize: wideFontSize32(),
                    color: 'rgba(85, 87, 89, 1)'
                },
                format: '{value:%d}',
                autoRotation: undefined
            },
            tickInterval: Day1Tick,
            lineColor: 'rgba(205, 206, 209, 1)',
            tickColor: 'rgba(205, 206, 209, 1)'
        },

    yAxis: {
        title: {
            text: ''
        },
        labels: {
            style: {
                fontSize: wideFontSize32()
            },
        }
    },

    tooltip:  applyTip((ctx) =>
        <BaseChartTooltip
            elements={
                ctx.points?.map((point, id) => ({
                    color: point.color as string,
                    name: point.series.name,
                    dimension: 'Руб.',
                    area: point.y || 0,
                    id: `${point.series.name}-${id}`
                })) || []
            }
            cellsInRow={2}
            RowFormatter={RowWithAreaFormatter}
        />, {
        shared: true,
        outside: false,
    }),

    plotOptions: {
        series: {
            borderWidth: 0,
        },
        column: {
            shadow: false,
            borderWidth: 0,
        }

    },
    credits: {
        enabled: false
    },
    series: [
        {
            name: 'План',
            id: 'plan',
            color: '#86CE52',
            type: 'line',
            opacity: 1,
            data: category.byDays.map(x => x[0]),
            pointIntervalUnit: 'day',
            marker: {
                enabled: false,
                symbol: 'circle'
            },
            lineWidth: checkWindowSize(3, 2)
        },
        {
            name: 'Факт',
            id: 'fact',
            color: '#FE7C66',
            type: 'line',
            data: category.byDays.map(x => x[1]),
            pointIntervalUnit: 'day',
            marker: {
                enabled: false,
                symbol: 'circle'
            },
            lineWidth: checkWindowSize(3, 2)
        }
    ]
})

interface ExpensesByMonthDashboardProps {
    onSelect: (title: string) => void,
    activeCategory?: ExpensesDetalizationData,
    categories: ExpensesDetalizationData[]
}

const monthNames = monthsForLocale()

const ExpensesChart: FC<{ chartOptions: Highcharts.Options, currentMonth: string, activeCategory?: ExpensesDetalizationData }> = ({
                                                                                                                                      currentMonth,
                                                                                                                                      chartOptions,
                                                                                                                                      activeCategory
                                                                                                                                  }) => {
    const [chartApi, setChartApi] = useState<Highcharts.Chart>()


    const { selected } = useTypedSelector(state => state.cropSelector);

    if (selected && array.isEmpty(selected)) {
        return (
            <Empty name={currentMonth}/>
        )
    }

    return <Sticker>
        <FlexLine>
            <div className={st.chart_container}>
                <div className={st.chart_header}>
                    <span className={st.title}>Затраты за {currentMonth} {activeCategory && <Badge category={activeCategory}/>}</span>
                    <span className={st.category_title}>{activeCategory?.totalFact !== undefined && formatNumberIntl(activeCategory.totalFact)} руб.</span>
                </div>
                <div className={st.chart}>
                    <LinearChart
                        containerProps={{style: {height: "100%", width: "100%", paddingTop: '25px'}}}
                        callback={setChartApi}
                        options={chartOptions}
                    >
                    </LinearChart>
                </div>
                <div className={st.legend}>
                    <PlanFactLegend chartApi={chartApi} />
                </div>
            </div>
        </FlexLine>
    </Sticker>
}
export const expensesDetalizationSelector: ApiSelect<ExpensesDetalizationState> = createSelector(
    (state: RootState) => state.dashExpensesDetalization,
    identity<ExpensesDetalizationState>
)
// const ExpensesChartWithLoading = stickerLoadingWithCrop(expensesDetalizationSelector, (actions) =>
//     (p, params) => actions.fetchExpensesDetalization({ ...p, month: Number(params.monthId) }))(ExpensesChart)

const ExpensesChartWithLoading = stickerLoadingWithCrop(
    expensesDetalizationSelector,
    actions => (p, params) => actions.fetchExpensesDetalization({ ...p, month: Number(params.monthId) })
)(ExpensesChart)

const ExpensesByMonthDashboard: FC<ExpensesByMonthDashboardProps> = (props) => {
    const { categories, onSelect, activeCategory } = props;
    const { monthId } = useParams()
    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({ title: { text: '' } })
    const { selected } = useTypedSelector(state => state.cropSelector);
    const renderCategory = (c: ExpensesDetalizationData, idx: number) => {
        const isEmptyData = selected && array.isEmpty(selected);
        const percent = c.totalFact / categories.reduce((acc, n) => acc + n.totalFact, 0) * 100 || 0;
        return <Category category={c}
                         key={idx}
                         percent={percent > 0 ? Math.max(Math.floor(percent), 1) : percent}
                         isEmptyData={isEmptyData}
                         isActive={activeCategory?.title === c.title}
                         onSelect={() => onSelect(c.title)}></Category>
    }

    useEffect(() => {
        if (activeCategory) {
            setChartOptions(defaultOptions(activeCategory, Number(monthId), categories))
        }
    }, [activeCategory])

    const currentMonth = monthNames[Number(monthId) - 1]

    return (
        <>
            <FlexLine className={st.categories}>
                {categories.map((c, idx) => renderCategory(c, idx))}
            </FlexLine>
            <ExpensesChartWithLoading chartOptions={chartOptions}
                                      currentMonth={currentMonth}
                                      activeCategory={activeCategory}></ExpensesChartWithLoading>
        </>
    );
};

export default ExpensesByMonthDashboard;
